import { NavigationHistory } from './NavigationHistory';
import { Logger } from './Logger';
import { parseCurrentURL, type ParsedURL } from './URLDataParser';
import { oemConfig } from './OEMConfig';
import { tenantService } from './TenantService';
import type { BaseForm } from '../forms/BaseForm';

const logger = Logger.scope('FormLoader');

// Vite glob imports for tenant forms - statically analyzed at build time
// Each tenant needs its own glob pattern for Vite to bundle correctly
const footballForms = import.meta.glob('../tenants/football/forms/*.ts') as Record<string, () => Promise<FormModule>>;
const specificationForms = import.meta.glob('../tenants/specification/forms/*.ts') as Record<string, () => Promise<FormModule>>;
const trisysForms = import.meta.glob('../tenants/trisys-2026-01-13/forms/*.ts') as Record<string, () => Promise<FormModule>>;

// Interface for form modules that export an init function
interface FormModule {
  init: () => Promise<void>;
}

// Store the current form instance for cleanup
let currentFormInstance: BaseForm | null = null;

// Store URL data captured before navigation (for form initialization)
let pendingURLData: Record<string, unknown> | null = null;

/**
 * Get and clear the pending URL data (called by BaseForm.init)
 */
export function consumePendingURLData<T = Record<string, unknown>>(): T | null {
  const data = pendingURLData as T | null;
  pendingURLData = null;
  return data;
}

/**
 * Set the current form instance (called by forms after initialization)
 */
export function setCurrentForm(form: BaseForm): void {
  currentFormInstance = form;
}

/**
 * Get the current form instance
 * Returns null if no form is currently loaded
 */
export function getCurrentForm(): BaseForm | null {
  return currentFormInstance;
}

/**
 * FormLoader - Handles loading and initializing forms
 * Forms are loaded dynamically based on the form name without relying on build-time glob patterns
/**
 * FormLoader - Handles loading and displaying forms in the main content area
 * 
 * IMPORTANT: Form loading is triggered EXPLICITLY, not automatically.
 * The constructor only sets up infrastructure (navigation history, UI handlers).
 * Actual form loading happens via:
 *   - loadInitialForm() - called once at app startup for normal mode
 *   - load(formName) - called explicitly when navigating to a form
 * 
 * This separation prevents accidental duplicate loading which would:
 *   - Double API calls to backend
 *   - Double initialization time
 *   - Create race conditions with data operations
 */
export class FormLoader {
  private initialized = false;

  constructor(private readonly containerId: string) {
    // Initialize navigation history (handles browser back/forward)
    NavigationHistory.init(
      (formName: string) => {
        // Load form without adding to history (already handled by popstate)
        this.loadWithoutHistory(formName);
      },
      () => {
        // Handle application exit
        this.handleApplicationExit();
      }
    );

    // Setup history dropdown UI updates
    this.setupHistoryUI();
  }

  /**
   * Load the initial form from URL hash (call ONCE at app startup)
   * 
   * This is separated from the constructor to prevent automatic loading
   * which could cause duplicate loads in popup mode or other special cases.
   */
  public loadInitialForm(): void {
    if (this.initialized) {
      logger.warn('loadInitialForm called more than once - ignoring');
      return;
    }
    this.initialized = true;

    // [FORMLOADER DEBUG] Log full URL state
    logger.info('[FORMLOADER DEBUG] loadInitialForm called');
    logger.info('[FORMLOADER DEBUG] pathname:', window.location.pathname);
    logger.info('[FORMLOADER DEBUG] hash:', window.location.hash);
    logger.info('[FORMLOADER DEBUG] href:', window.location.href);

    // CRITICAL: Capture URL data BEFORE navigation changes the URL
    const parsed = parseCurrentURL();
    logger.info('[FORMLOADER DEBUG] parsed:', JSON.stringify(parsed));
    
    if (parsed.data) {
      pendingURLData = parsed.data;
      logger.debug('Captured URL data for form:', parsed.data);
    }

    const initialForm = parsed.formName;
    if (initialForm) {
      logger.info('[FORMLOADER DEBUG] Loading initial form:', initialForm);
      this.load(initialForm);
    } else {
      logger.info('[FORMLOADER DEBUG] No initial form in URL hash');
    }
  }

  /**
   * Load and display a form
   */
  public async load(formNameWithParams: string): Promise<void> {
    logger.debug(`Loading form "${formNameWithParams}"`);
    
    // Parse form name (strip query params for disability check)
    const [baseFormName] = formNameWithParams.split('?');
    
    // Check if form is disabled for current tenant
    if (oemConfig.isFormDisabled(baseFormName)) {
      logger.warn(`Form "${baseFormName}" is disabled for current tenant`);
      this.showFormDisabled(baseFormName);
      return;
    }
    
    // Add to navigation history (keep full path with params for bookmarkability)
    NavigationHistory.push(formNameWithParams);
    
    await this.loadWithoutHistory(formNameWithParams);
  }

  /**
   * Load form without adding to navigation history (used by popstate)
   */
  private async loadWithoutHistory(formNameWithParams: string): Promise<void> {
    const container = document.getElementById(this.containerId);
    if (!container) {
      logger.error(`Container ${this.containerId} not found`);
      return;
    }

    // Parse form name and query parameters
    // Input: "nationality?id=abc123" → formName: "nationality", params: {id: "abc123"}
    const [formName, queryString] = formNameWithParams.split('?');
    
    // Store URL data for the form to consume (via consumePendingURLData)
    if (queryString) {
      const params = new URLSearchParams(queryString);
      pendingURLData = Object.fromEntries(params);
      logger.debug('Captured URL params for form:', pendingURLData);
    }

    logger.debug(`Loading form "${formName}" (from "${formNameWithParams}")`);

    // Fade out current form
    await this.fadeOut(container);

    // Destroy the current form instance if it exists
    if (currentFormInstance) {
      try {
        currentFormInstance.destroy();
        logger.debug('Destroyed previous form instance');
      } catch (error) {
        logger.error('Error destroying previous form:', error);
      }
      currentFormInstance = null;
    }

    // Clean up any global elements from previous forms (e.g., FAB containers)
    this.cleanupGlobalElements();

    try {
      // Try tenant-specific form first, then fallback to shared forms
      const tenantId = tenantService.getTenantId();
      const tenantPath = `/oem/${tenantId}/forms/${formName}.html`;
      const sharedPath = `/forms/${formName}.html`;
      
      logger.debug(`Attempting to load form: ${formName}`);
      logger.debug(`Tenant: ${tenantId}, trying: ${tenantPath}`);
      
      let res = await fetch(tenantPath);
      let formContent = await res.text();
      let isFromTenant = false;
      
      // Check if we got the actual form or if dev server returned index.html
      const isIndexHtml = formContent.includes('<!doctype html') || formContent.includes('<!DOCTYPE html');
      
      if (!res.ok || isIndexHtml) {
        // Fallback to shared forms directory
        logger.debug(`Tenant form not found (status=${res.status}, isIndex=${isIndexHtml}), trying shared: ${sharedPath}`);
        res = await fetch(sharedPath);
        formContent = await res.text();
        isFromTenant = false;
      } else {
        logger.debug(`Loading tenant-specific form: ${tenantPath}`);
        isFromTenant = true;
      }
      
      logger.debug(`Fetch response for "${formName}": status=${res.status}, ok=${res.ok}, isFromTenant=${isFromTenant}`);
      
      if (!res.ok) {
        logger.warn(`Form "${formName}" not found (HTTP ${res.status})`);
        this.showFormNotFound(formName);
        this.updateActiveState(formName);
        await this.fadeIn(container);
        return;
      }

      // Check again if we got the actual form or if dev server returned index.html
      if (formContent.includes('<!doctype html') || formContent.includes('<!DOCTYPE html')) {
        logger.warn(`Form "${formName}" not found - dev server returned index.html`);
        this.showFormNotFound(formName);
        this.updateActiveState(formName);
        await this.fadeIn(container);
        return;
      }

      // Wrap content in inner div for mobile padding
      container.innerHTML = `<div class="form-inner">${formContent}</div>`;

      // Scroll to top of form
      container.scrollTop = 0;

      // Update active state in navigation
      this.updateActiveState(formName);

      // Fade in new form immediately (shows skeleton if form has one)
      await this.fadeIn(container);

      // Ensure the browser has painted the skeleton before starting form initialization
      // This guarantees the skeleton is visible during API calls
      await new Promise<void>((resolve) => requestAnimationFrame(() => resolve()));

      // Load and initialize form module if exists (skeleton visible during this)
      await this.initializeFormModule(formName, isFromTenant);
      logger.debug(`Successfully loaded form "${formName}"`);
    } catch (error) {
      logger.error(`Error loading form "${formName}":`, error);
      this.showFormNotFound(formName);
      this.updateActiveState(formName);
      await this.fadeIn(container);
    }
  }

  /**
   * Fade out the container
   */
  private fadeOut(container: HTMLElement): Promise<void> {
    return new Promise((resolve) => {
      container.style.transition = 'opacity 50ms ease-out';
      container.style.opacity = '0';
      setTimeout(resolve, 50);
    });
  }

  /**
   * Fade in the container
   */
  private fadeIn(container: HTMLElement): Promise<void> {
    return new Promise((resolve) => {
      container.style.transition = 'opacity 50ms ease-in';
      container.style.opacity = '1';
      setTimeout(resolve, 50);
    });
  }

  /**
   * Clean up any global elements left by previous forms
   * This is a fallback - forms should clean up in their destroy() method,
   * but this catches any orphaned elements (e.g., FAB containers)
   */
  private cleanupGlobalElements(): void {
    // Remove FAB global container
    const fabContainer = document.getElementById('fab-global-container');
    if (fabContainer) {
      fabContainer.remove();
      logger.debug('Cleaned up fab-global-container');
    }
  }

  /**
   * Display a "form not found" message
   */
  private showFormNotFound(formName: string): void {
    const container = document.getElementById(this.containerId);
    if (!container) return;

    container.innerHTML = `
      <div class="form-inner" style="background: var(--fallback-b1, oklch(var(--b1)));">
        <div class="alert alert-error shadow-lg mb-6">
          <svg xmlns="http://www.w3.org/2000/svg" class="stroke-current shrink-0 h-6 w-6" fill="none" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
          <div>
            <h3 class="font-bold">Form Not Found</h3>
            <div class="text-sm">The form <code>${formName}</code> could not be loaded.</div>
          </div>
        </div>
        
        <div class="card bg-base-200 shadow-xl">
          <div class="card-body">
            <h2 class="card-title">
              <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
              What happened?
            </h2>
            <p>This form is referenced in the navigation but hasn't been created yet.</p>
            
            <div class="divider">Next Steps</div>
            
            <div class="space-y-2">
              <p class="font-semibold">Create these files:</p>
              <div class="mockup-code text-xs">
                <pre data-prefix="1"><code>public/forms/${formName}.html</code></pre>
                <pre data-prefix="2"><code>src/forms/${formName}.ts</code></pre>
              </div>
              <p class="text-sm opacity-70">Extend the BaseForm class for consistent UI</p>
            </div>

            <div class="card-actions justify-end mt-4">
              <button class="btn btn-primary btn-sm" onclick="window.OpenForm('${this.getDefaultDashboard()}')">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" /></svg>
                Go to Dashboard
              </button>
            </div>
          </div>
        </div>
      </div>
    `;

    // Scroll to top of form
    container.scrollTop = 0;
  }

  /**
   * Display a "form disabled" message for tenant-restricted forms
   */
  private showFormDisabled(formName: string): void {
    const container = document.getElementById(this.containerId);
    if (!container) return;

    container.innerHTML = `
      <div class="form-inner" style="background: var(--fallback-b1, oklch(var(--b1)));">
        <div class="alert alert-warning shadow-lg mb-6">
          <svg xmlns="http://www.w3.org/2000/svg" class="stroke-current shrink-0 h-6 w-6" fill="none" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" /></svg>
          <div>
            <h3 class="font-bold">Feature Not Available</h3>
            <div class="text-sm">The form <code>${formName}</code> is not available for your account.</div>
          </div>
        </div>
        
        <div class="card bg-base-200 shadow-xl">
          <div class="card-body">
            <h2 class="card-title">
              <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" /></svg>
              Access Restricted
            </h2>
            <p>This feature is not enabled for your organization's subscription.</p>
            
            <div class="divider">Need Access?</div>
            
            <p class="text-sm opacity-70">
              Contact your administrator or our support team to enable this feature.
            </p>

            <div class="card-actions justify-end mt-4">
              <button class="btn btn-primary btn-sm" onclick="window.OpenForm('${this.getDefaultDashboard()}')">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" /></svg>
                Go to Dashboard
              </button>
            </div>
          </div>
        </div>
      </div>
    `;

    // Scroll to top of form
    container.scrollTop = 0;
  }

  /**
   * Get the default dashboard form name for the current tenant
   * Falls back to 'dashboard' if OEM config is not available
   */
  private getDefaultDashboard(): string {
    try {
      // Try startScreen first (the tenant's home screen)
      const startScreen = oemConfig.getStartScreen();
      if (startScreen) {
        return startScreen;
      }
      // Fallback to postLoginForm if startScreen not set
      const postLoginForm = oemConfig.getPostLoginForm();
      if (postLoginForm) {
        return postLoginForm;
      }
    } catch {
      // OEM config not loaded yet
    }
    // Ultimate fallback
    return 'dashboard';
  }

  /**
   * Update active state in sidebar navigation
   */
  private updateActiveState(formName: string): void {
    document.querySelectorAll('[data-form]').forEach(a => 
      (a as HTMLElement).classList.remove('active')
    );
    
    const active = document.querySelector(`[data-form="${formName}"]`);
    if (active) (active as HTMLElement).classList.add('active');

    // Dispatch event for dynamic sidebar items
    window.dispatchEvent(new CustomEvent('formLoaded', { detail: { formName } }));
  }

  /**
   * Load and initialize the TypeScript module for a form
   * Uses dynamic import to load form modules at runtime
   * Tries tenant-specific modules first, then falls back to shared forms
   */
  private async initializeFormModule(formName: string, isFromTenant: boolean = false): Promise<void> {
    try {
      const tenantId = tenantService.getTenantId();
      
      // Use dynamic import to load the form module
      // Vite requires separate import patterns for different folder depths
      let mod;
      
      // If HTML was loaded from tenant directory, try tenant module first
      if (isFromTenant) {
        try {
          logger.debug(`Trying tenant module for: ${tenantId}/${formName}`);
          
          // Use pre-registered glob imports for each tenant
          if (tenantId === 'football') {
            const modulePath = `../tenants/football/forms/${formName}.ts`;
            const moduleLoader = footballForms[modulePath];
            if (moduleLoader) {
              mod = await moduleLoader();
            }
          } else if (tenantId === 'specification') {
            const modulePath = `../tenants/specification/forms/${formName}.ts`;
            const moduleLoader = specificationForms[modulePath];
            if (moduleLoader) {
              mod = await moduleLoader();
            }
          } else if (tenantId === 'trisys-2026-01-13') {
            const modulePath = `../tenants/trisys-2026-01-13/forms/${formName}.ts`;
            const moduleLoader = trisysForms[modulePath];
            if (moduleLoader) {
              mod = await moduleLoader();
            }
          }
          // Add more tenants as needed
          
          if (mod) {
            logger.debug(`Loaded tenant module for "${formName}"`);
          }
        } catch (tenantError: any) {
          // If tenant module not found, fall through to shared forms logic below
          logger.debug(`Tenant module not found, trying shared forms: ${tenantError.message}`);
        }
      }
      
      // If no tenant module loaded, try shared forms
      if (!mod) {
        if (formName.startsWith('samples/')) {
          // Handle samples subfolder - Vite needs explicit path pattern
          const sampleName = formName.replace('samples/', '');
          mod = await import(`../forms/samples/${sampleName}.ts`);
        } else if (formName.startsWith('auth/')) {
          // Handle auth subfolder
          const authName = formName.replace('auth/', '');
          mod = await import(`../forms/auth/${authName}.ts`);
        } else {
          // Handle root-level forms
          mod = await import(`../forms/${formName}.ts`);
        }
      }
      
      if (mod && typeof mod.init === "function") {
        await mod.init();
        logger.debug(`Initialized module for form "${formName}"`);
      } else {
        logger.warn(`Module for "${formName}" has no init function`);
      }
    } catch (error: any) {
      // Check if it's a module not found error
      if (error.message && error.message.includes('Unknown variable dynamic import')) {
        logger.debug(`No TypeScript module found for form: ${formName} (HTML-only form)`);
      } else {
        logger.error(`Error initializing form module ${formName}:`, error);
      }
      // Don't throw - allow the app to continue functioning
    }
  }

  /**
   * Setup history dropdown UI
   */
  private setupHistoryUI(): void {
    window.addEventListener('navigationHistoryChanged', (event: Event) => {
      const customEvent = event as CustomEvent;
      const history = customEvent.detail.history;
      this.updateHistoryDropdown(history);
    });
  }

  /**
   * Update the history dropdown in the navbar
   */
  private updateHistoryDropdown(history: any[]): void {
    const dropdown = document.getElementById('history-dropdown');
    if (!dropdown) return;

    if (history.length === 0) {
      dropdown.innerHTML = `
        <li class="menu-title">Recent Forms</li>
        <li><a class="text-sm opacity-60">No history yet</a></li>
      `;
      return;
    }

    const items = history.map((entry, index) => {
      const iconClass = entry.icon || 'fas fa-file-alt';
      return `
      <li>
        <a onclick="document.activeElement?.blur(); window.OpenForm('${entry.formName}')" class="flex items-center gap-2">
          <i class="${iconClass} text-xs flex-shrink-0 w-4 text-center"></i>
          <span class="flex-1 truncate">${entry.title}</span>
          ${index === 0 ? '<span class="badge badge-primary badge-xs flex-shrink-0">Current</span>' : ''}
        </a>
      </li>
    `;
    }).join('');

    dropdown.innerHTML = `
      <li class="menu-title sticky top-0 bg-base-200 z-10">Recent Forms</li>
      <div class="history-items-container max-h-[60vh] overflow-y-auto entity-form-scrollable">
        ${items}
      </div>
      <div class="history-footer sticky bottom-0 bg-base-200 pt-1">
        ${history.length > 1 ? `
          <li class="border-t border-base-300 pt-2">
            <a onclick="document.activeElement?.blur(); window.history.back()" class="text-sm"><i class="fas fa-arrow-left"></i> Go Back</a>
          </li>
        ` : ''}
        <li class="border-t border-base-300 pt-2">
          <a id="clear-history-btn" class="text-sm"><i class="fas fa-trash"></i> Clear History</a>
        </li>
      </div>
    `;

    // Bind clear history button (NavigationHistory not available in global scope)
    const clearBtn = dropdown.querySelector('#clear-history-btn');
    clearBtn?.addEventListener('click', () => {
      document.activeElement instanceof HTMLElement && document.activeElement.blur();
      NavigationHistory.clear();
      location.reload();
    });
  }

  /**
   * Handle application exit
   * This will be called when user tries to navigate away from the application
   */
  private handleApplicationExit(): void {
    logger.debug('Application exit triggered');
    
    // Future: Add logout logic here
    // - Clear session tokens
    // - Call REST API logout endpoint
    // - Clear local storage
    // - Perform cleanup tasks
    
    logger.info('User is leaving the application');
    // TODO: Add REST API logout when implemented
  }
}